import { useRef } from 'react';
import { exportLayerOriginInfo, getFramework, updateLayerPosition, useIdentity } from '@/framework';
import type { ITouchKitEvent } from '@/utils/event-kit';
import {
    BaseLayerViewType,
    MOVING_ABSORB_DISTANCE,
    REFERENCE_LINE_EVENT_NAMES,
    SMART_GUIDE_EVENT_NAMES,
    type ILayerProps,
} from '@/version-types';
import { useMovingProxy } from './use-move-proxy';

export const useMoveOperate = () => {
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<ReturnType<typeof exportLayerOriginInfo>>();
    const framework = getFramework(identity);
    const { proxyMoving, proxyBeforeMoving, proxyAfterMoving } = useMovingProxy();

    return {
        /**
         * 移动之前的准备工作，用于存储一些值
         *
         * @param layerId - 图层 ID
         */
        beforeMove(layerId: string) {
            const layer = framework.getLayer(layerId, true);
            oldInfo.current = layer;
            originLayerInfo.current = exportLayerOriginInfo(identity, layerId);
            framework.events.emitBeforeBaseOperate(layer, BaseLayerViewType.MOVE);
            if (MOVING_ABSORB_DISTANCE) {
                proxyBeforeMoving();
            }
        },
        /**
         * 移动 —— 移动之前需要先确保 `beforeMove` 函数的调用
         *
         * @param layerId - 图层 ID
         * @param e - 触摸事件对象
         */
        move(layerId: string, e: ITouchKitEvent) {
            if (!oldInfo.current || !originLayerInfo.current) return;
            if (!framework.getLayer(layerId)) return;

            framework.operate.setIsBaseOperate(true);
            const scale = framework.stage.getScale();
            let x = e.displacementX / scale;
            let y = e.displacementY / scale;
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;

            // 吸附
            if (MOVING_ABSORB_DISTANCE) {
                const proxyResult = proxyMoving(x, y, oldInfo.current, e);
                x = proxyResult.x;
                y = proxyResult.y;
            }

            updateLayerPosition(identity, layerId, x, y, originInfo, childOriginInfoMap, {
                isStart: e.isStart,
                isEnd: e.isEnd,
            });

            if (MOVING_ABSORB_DISTANCE) {
                proxyAfterMoving(e);
            }

            if (e.isEnd) {
                framework.operate.setIsBaseOperate(false);
                // 拖拉图片图层替换，会改变激活图层
                const activeId = framework.operate.getActivePickLayer()?.id;
                if ((activeId && activeId === layerId) || framework.checkIsGroupExtension(oldInfo.current)) {
                    const layer = framework.getLayer(layerId, true);
                    framework.stage.addOrUpdate(layer);
                    if (oldInfo.current) {
                        framework.history.write(oldInfo.current);
                    }
                    framework.operate.setActiveLayer(layerId);
                }
                // 清除智能辅助线
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_BLOCKS, []);
                framework.events.emit(SMART_GUIDE_EVENT_NAMES.SET_LINES, []);
                framework.events.emit(REFERENCE_LINE_EVENT_NAMES.HIGHLIGHT, []);
            }
        },
    };
};
